import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { VitoguideBackendSettingsApiPicker } from '@vi-iot/client';

import { retryFailedCall } from '@common';

import {
  backendStorageSessionsKey,
  localStorageSessionsKey,
  RemoteDiagnosticsActiveSession,
} from '@store';

import { AppInsightsMonitoringService } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP } from 'src/app/shared/services/app-insights/events/remote-diagnostics.events';
import { LogLevelEnum } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';

import { BackendStorageActions } from './backend-storage.actions';

@Injectable()
export class BackendStorageEffects {
  getKeyValue$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BackendStorageActions.GetKeyValue),
      switchMap((action) =>
        this.settingsApiPicker
          .instance()
          .getKeyValueStorage(action.key)
          .pipe(
            retryFailedCall(3),
            tap((response) => {
              if (action.key === backendStorageSessionsKey) {
                this.clearLocalStorageFromUnusedSessions(response.activeSessions);
              }
            }),
            map((response) =>
              BackendStorageActions.GetKeyValueSuccess({ key: action.key, value: response })
            ),
            catchError(() => of(BackendStorageActions.GetKeyValueError()))
          )
      )
    );
  });

  getKeyValueWithoutPropagation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BackendStorageActions.GetKeyValueWithoutPropagation),
      switchMap((action) =>
        this.settingsApiPicker
          .instance()
          .getKeyValueStorage(action.key)
          .pipe(
            retryFailedCall(3),
            map((response) =>
              BackendStorageActions.GetKeyValueWithoutPropagationSuccess({
                key: action.key,
                value: response,
              })
            ),
            catchError(() => of(BackendStorageActions.GetKeyValueError()))
          )
      )
    );
  });

  setKeyValue$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(BackendStorageActions.SetKeyValue),
      switchMap((action) =>
        this.settingsApiPicker
          .instance()
          .setKeyValueStorage(action.key, action.value)
          .pipe(
            retryFailedCall(3),
            map(() =>
              BackendStorageActions.SetKeyValueSuccess({
                key: action.key,
                value: action.value,
              })
            ),
            catchError((error) => {
              if (action.key === backendStorageSessionsKey) {
                this.appInsights.logEvent(
                  APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.UPDATE_ACTIVE_SESSIONS_FAILED,
                  LogLevelEnum.ERROR,
                  { error }
                );
              }

              // prevent unnecessary logout during automatic relogin - 401 triggers CSRF refetch + page refresh
              // this may cause problems only when token expire during the conneciton sequence
              if (error.statusCode !== 401) {
                return of(
                  BackendStorageActions.SetKeyValueError({ gatewaySerial: action.gatewaySerial })
                );
              }
            })
          )
      )
    );
  });

  private clearLocalStorageFromUnusedSessions(sessions: RemoteDiagnosticsActiveSession[]): void {
    Object.keys(localStorage).forEach((key) => {
      const isKeyValid = key.startsWith(localStorageSessionsKey);
      const isKeyNotRepeatedInResponse = !sessions.some((session) => {
        return localStorageSessionsKey + session.gatewaySerial === key;
      });

      if (isKeyValid && isKeyNotRepeatedInResponse) {
        localStorage.removeItem(key);
      }
    });
  }

  constructor(
    private settingsApiPicker: VitoguideBackendSettingsApiPicker,
    private actions$: Actions,
    private appInsights: AppInsightsMonitoringService
  ) {}
}
